<script setup lang="ts">
import { ref, computed } from 'vue'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Loader2, Check, ChevronsUpDown, User, Building2 } from 'lucide-vue-next'
import { cn } from '@/lib/utils'
import { API, HOST } from '@/utils/http'
import type { BridgeCustomer, BridgeOrganization, BridgeTransfer, SourcePaymentRail, DestinationPaymentRail } from '@/types/bridge'

interface ApprovedCustomer {
  id: number
  type: 'individual' | 'business'
  name: string
  email?: string
  bridge_customer_id: string
}

const emit = defineEmits<{
  (e: 'transfer-created', transfer: BridgeTransfer): void
}>()

// Dialog state
const open = ref(false)

// Form fields
const selectedSenderId = ref<string>('')
const selectedReceiverId = ref<string>('')
const amount = ref('')
const sourcePaymentRail = ref<SourcePaymentRail>('ach_push')
const destinationPaymentRail = ref<DestinationPaymentRail>('ethereum')

// Popover states
const senderOpen = ref(false)
const receiverOpen = ref(false)

// Customers state (approved only)
const approvedCustomers = ref<ApprovedCustomer[]>([])
const loadingCustomers = ref(false)

// Common state
const creating = ref(false)
const error = ref<string | null>(null)

// Get selected sender
const selectedSender = computed(() => {
  if (!selectedSenderId.value) return null
  return approvedCustomers.value.find(c => String(c.id) === selectedSenderId.value)
})

// Get selected receiver
const selectedReceiver = computed(() => {
  if (!selectedReceiverId.value) return null
  return approvedCustomers.value.find(c => String(c.id) === selectedReceiverId.value)
})

// Check if form is valid
const isFormValid = computed(() => {
  return selectedSenderId.value &&
    selectedReceiverId.value &&
    amount.value &&
    parseFloat(amount.value) > 0 &&
    sourcePaymentRail.value &&
    destinationPaymentRail.value
})

// Filter function for command
const filterCustomers = (list: ApprovedCustomer[], search: string) => {
  const term = search.toLowerCase()
  return list.filter(c =>
    c.name.toLowerCase().includes(term) ||
    c.email?.toLowerCase().includes(term) ||
    String(c.id).includes(term)
  )
}

// Fetch approved customers (individuals + organizations)
const fetchApprovedCustomers = async () => {
  loadingCustomers.value = true
  try {
    const [customersRes, orgsRes] = await Promise.all([
      API({ method: 'GET', url: `${HOST}/payments/bridge/customers` }),
      API({ method: 'GET', url: `${HOST}/payments/bridge/organizations` })
    ])

    const individuals: ApprovedCustomer[] = (customersRes.data as BridgeCustomer[])
      .filter(c => c.kyc_status === 'approved' && c.bridge_customer_id)
      .map(c => ({
        id: c.id,
        type: 'individual' as const,
        name: c.member_display_name || c.member_email || 'Unknown',
        email: c.member_email || undefined,
        bridge_customer_id: c.bridge_customer_id!
      }))

    const businesses: ApprovedCustomer[] = (orgsRes.data as BridgeOrganization[])
      .filter(o => o.kyc_status === 'approved' && o.bridge_customer_id)
      .map(o => ({
        id: o.id,
        type: 'business' as const,
        name: o.organization_name || `Org #${o.organization_id}`,
        email: o.contact_email || undefined,
        bridge_customer_id: o.bridge_customer_id!
      }))

    approvedCustomers.value = [...individuals, ...businesses]
  } catch (err: any) {
    console.error('Error fetching customers:', err)
  } finally {
    loadingCustomers.value = false
  }
}

// Reset form
const resetForm = () => {
  selectedSenderId.value = ''
  selectedReceiverId.value = ''
  amount.value = ''
  sourcePaymentRail.value = 'ach_push'
  destinationPaymentRail.value = 'ethereum'
  error.value = null
}

// Create transfer
const createTransfer = async () => {
  error.value = null

  if (!selectedSenderId.value) {
    error.value = 'Please select a sender'
    return
  }
  if (!selectedReceiverId.value) {
    error.value = 'Please select a receiver'
    return
  }
  if (!amount.value || parseFloat(amount.value) <= 0) {
    error.value = 'Please enter a valid amount'
    return
  }

  creating.value = true

  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/payments/bridge/transfers`,
      data: {
        sender_customer_id: parseInt(selectedSenderId.value),
        receiver_customer_id: parseInt(selectedReceiverId.value),
        amount: amount.value,
        source_payment_rail: sourcePaymentRail.value,
        destination_payment_rail: destinationPaymentRail.value
      }
    })

    emit('transfer-created', response.data)
    open.value = false
    resetForm()
  } catch (err: any) {
    console.error('Error creating transfer:', err)
    error.value = err.response?.data?.detail || 'Failed to create transfer'
  } finally {
    creating.value = false
  }
}

// Fetch customers on init
fetchApprovedCustomers()
</script>

<template>
  <Dialog v-model:open="open">
    <DialogTrigger as-child>
      <Button>
        New Transfer
      </Button>
    </DialogTrigger>
    <DialogContent class="sm:max-w-[500px]">
      <DialogHeader>
        <DialogTitle>Create Transfer</DialogTitle>
        <DialogDescription>
          Create a new transfer between Bridge customers. Only KYC-approved customers are shown.
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-4 py-4">
        <!-- Error message -->
        <div v-if="error" class="p-3 bg-destructive/10 border border-destructive/30 rounded text-destructive text-sm">
          {{ error }}
        </div>

        <!-- Sender -->
        <div>
          <Label class="mb-2 block">Sender</Label>
          <Popover v-model:open="senderOpen">
            <PopoverTrigger as-child>
              <Button
                variant="outline"
                role="combobox"
                :aria-expanded="senderOpen"
                class="w-full justify-between"
                :disabled="loadingCustomers"
              >
                <span v-if="loadingCustomers">Loading customers...</span>
                <span v-else-if="selectedSender" class="flex items-center gap-2 truncate">
                  <User v-if="selectedSender.type === 'individual'" class="h-4 w-4 flex-shrink-0 text-primary" />
                  <Building2 v-else class="h-4 w-4 flex-shrink-0 text-purple-500" />
                  <span class="font-medium">{{ selectedSender.name }}</span>
                </span>
                <span v-else class="text-muted-foreground">Select sender...</span>
                <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent class="w-[400px] p-0">
              <Command :filter-function="filterCustomers">
                <CommandInput placeholder="Search by name or email..." />
                <CommandList>
                  <CommandEmpty>No approved customers found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      v-for="customer in approvedCustomers"
                      :key="customer.id"
                      :value="customer"
                      @select="() => {
                        selectedSenderId = String(customer.id)
                        senderOpen = false
                      }"
                    >
                      <Check
                        :class="cn(
                          'mr-2 h-4 w-4',
                          selectedSenderId === String(customer.id) ? 'opacity-100' : 'opacity-0'
                        )"
                      />
                      <div class="flex items-center gap-2 flex-1 min-w-0">
                        <User v-if="customer.type === 'individual'" class="h-4 w-4 flex-shrink-0 text-primary" />
                        <Building2 v-else class="h-4 w-4 flex-shrink-0 text-purple-500" />
                        <span class="font-medium truncate">{{ customer.name }}</span>
                        <span v-if="customer.email" class="text-xs text-muted-foreground truncate">
                          {{ customer.email }}
                        </span>
                      </div>
                    </CommandItem>
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <!-- Receiver -->
        <div>
          <Label class="mb-2 block">Receiver</Label>
          <Popover v-model:open="receiverOpen">
            <PopoverTrigger as-child>
              <Button
                variant="outline"
                role="combobox"
                :aria-expanded="receiverOpen"
                class="w-full justify-between"
                :disabled="loadingCustomers"
              >
                <span v-if="loadingCustomers">Loading customers...</span>
                <span v-else-if="selectedReceiver" class="flex items-center gap-2 truncate">
                  <User v-if="selectedReceiver.type === 'individual'" class="h-4 w-4 flex-shrink-0 text-primary" />
                  <Building2 v-else class="h-4 w-4 flex-shrink-0 text-purple-500" />
                  <span class="font-medium">{{ selectedReceiver.name }}</span>
                </span>
                <span v-else class="text-muted-foreground">Select receiver...</span>
                <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent class="w-[400px] p-0">
              <Command :filter-function="filterCustomers">
                <CommandInput placeholder="Search by name or email..." />
                <CommandList>
                  <CommandEmpty>No approved customers found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      v-for="customer in approvedCustomers"
                      :key="customer.id"
                      :value="customer"
                      @select="() => {
                        selectedReceiverId = String(customer.id)
                        receiverOpen = false
                      }"
                    >
                      <Check
                        :class="cn(
                          'mr-2 h-4 w-4',
                          selectedReceiverId === String(customer.id) ? 'opacity-100' : 'opacity-0'
                        )"
                      />
                      <div class="flex items-center gap-2 flex-1 min-w-0">
                        <User v-if="customer.type === 'individual'" class="h-4 w-4 flex-shrink-0 text-primary" />
                        <Building2 v-else class="h-4 w-4 flex-shrink-0 text-purple-500" />
                        <span class="font-medium truncate">{{ customer.name }}</span>
                        <span v-if="customer.email" class="text-xs text-muted-foreground truncate">
                          {{ customer.email }}
                        </span>
                      </div>
                    </CommandItem>
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <!-- Amount -->
        <div>
          <Label class="mb-2 block">Amount (USD)</Label>
          <Input
            v-model="amount"
            type="text"
            inputmode="decimal"
            placeholder="100.00"
            :disabled="creating"
          />
        </div>

        <!-- Payment Rails -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <Label class="mb-2 block">Source (Fiat)</Label>
            <Select v-model="sourcePaymentRail" :disabled="creating">
              <SelectTrigger>
                <SelectValue placeholder="Select source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ach_push">ACH Push</SelectItem>
                <SelectItem value="wire">Wire</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label class="mb-2 block">Destination (Crypto)</Label>
            <Select v-model="destinationPaymentRail" :disabled="creating">
              <SelectTrigger>
                <SelectValue placeholder="Select destination" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ethereum">Ethereum</SelectItem>
                <SelectItem value="polygon">Polygon</SelectItem>
                <SelectItem value="base">Base</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <!-- Footer with Create Button -->
      <div class="flex justify-end gap-3">
        <Button variant="outline" @click="open = false" :disabled="creating">
          Cancel
        </Button>
        <Button @click="createTransfer" :disabled="creating || !isFormValid">
          <Loader2 v-if="creating" class="mr-2 h-4 w-4 animate-spin" />
          Create Transfer
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
